import { apiRequest } from '../../shared/api/client';

/**
 * Receiving — mirrors Invetrak.Api.Endpoints.ReceiptEndpoints.
 *   GET    /api/v1/receipts                                     receipts:read
 *   GET    /api/v1/receipts/{id}                                receipts:read
 *   POST   /api/v1/receipts                                     receipts:post
 *   PUT    /api/v1/receipts/{id}                                receipts:post
 *   POST   /api/v1/receipts/{id}/post                           receipts:post
 *   POST   /api/v1/receipts/{id}/cancel                         receipts:post
 *   GET    /api/v1/receipts/{id}/lines                          receipts:read
 *   POST   /api/v1/receipts/{id}/lines                          receipts:post
 *   PUT    /api/v1/receipts/{id}/lines/{lineId}                 receipts:post
 *   DELETE /api/v1/receipts/{id}/lines/{lineId}                 receipts:post
 *   GET    /api/v1/purchase-orders/{id}/receivable-lines        receipts:read
 *
 * One permission covers building a draft and posting it, which is the server's split too: the
 * person keying a delivery in is the person booking it in.
 *
 * The batch number is never in a request body — it comes from the RECEIPT_BATCH sequence when the
 * batch is created, which is the whole reason nobody keeps a spreadsheet of used numbers.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/**
 * A draft, a document, or an abandoned draft.
 *
 * `posted` is terminal in both directions: the ledger has been written, and the receipt somebody
 * printed has to stay the receipt the system holds. Undoing one will be a REVERSAL — a new batch
 * pointing back at this one — rather than an edit.
 */
export type ReceiptStatus = 'open' | 'posted' | 'cancelled';

export interface ReceiptDto {
  receiptBatchId: string;
  batchNumber: string;
  warehouseId: string;
  warehouseCode: string;
  purchaseOrderId: string | null;
  poNumber: string | null;
  supplierId: string | null;
  supplierNumber: string | null;
  supplierName: string | null;
  status: ReceiptStatus;
  /** The driver's delivery note. Legacy has nowhere to record it. */
  packingSlipNumber: string | null;
  notes: string | null;
  /** Taken at POST time, from the order. Blank until then. */
  shipToName: string | null;
  shipToAddressLine1: string | null;
  shipToAddressLine2: string | null;
  shipToCity: string | null;
  shipToStateProvince: string | null;
  shipToPostalCode: string | null;
  createdAtUtc: string;
  createdByUserId: string | null;
  postedAtUtc: string | null;
  postedByUserId: string | null;
  lastModifiedUtc: string | null;
  archivedAtUtc: string | null;
  /** Set on a reversal, naming the batch it undoes. Nothing writes it yet. */
  reversesReceiptBatchId: string | null;
  lineCount: number;
  totalQuantity: number;
}

export interface ReceiptLineDto {
  receiptBatchLineId: string;
  receiptBatchId: string;
  lineNumber: number;
  purchaseOrderLineId: string | null;
  purchaseOrderLineNumber: number | null;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  locationId: string;
  locationCode: string;
  lotId: string | null;
  lotNumber: string | null;
  serialId: string | null;
  serialNumber: string | null;
  licensePlateId: string | null;
  licensePlateNumber: string | null;
  quantityReceived: number;
  uomId: string;
  uomCode: string;
  receivedByUserId: string | null;
  receivedAtUtc: string;
}

/**
 * What is still owed on one purchase order line.
 *
 * `pendingInOtherBatches` is the term that is easy to miss, and it is legacy's: without it two
 * people receiving the same delivery at two doors are each told the full quantity is due, and
 * between them book twice what arrived.
 *
 *   balanceDue = quantityOrdered − quantityReceived − pendingInOtherBatches − pendingInThisBatch
 */
export interface ReceivableLineDto {
  purchaseOrderLineId: string;
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  isLotControlled: boolean;
  isSerialized: boolean;
  tracksExpiration: boolean;
  quantityOrdered: number;
  quantityReceived: number;
  pendingInOtherBatches: number;
  pendingInThisBatch: number;
  balanceDue: number;
  uomId: string;
  uomCode: string;
  status: string;
}

export interface CreateReceiptRequest {
  warehouseId: string;
  purchaseOrderId: string;
  packingSlipNumber?: string | null;
  notes?: string | null;
}

export interface UpdateReceiptRequest {
  packingSlipNumber?: string | null;
  notes?: string | null;
}

export interface SaveReceiptLineRequest {
  purchaseOrderLineId: string;
  locationId: string;
  quantityReceived: number;
  /** Omitted means the unit the line was ordered in, which is what turns up. */
  uomId?: string | null;
  lotNumber?: string | null;
  serialNumber?: string | null;
  /** Only after a lot_needs_expiration refusal: the lot is new and the item tracks expiry. */
  lotExpirationDateUtc?: string | null;
  licensePlateId?: string | null;
  /** A pallet label that is not enrolled yet. Either this or licensePlateId, never both. */
  newLicensePlateNumber?: string | null;
}

export interface ReceiptQuery {
  warehouseId?: string;
  purchaseOrderId?: string;
  status?: string;
  openOnly?: boolean;
  includeArchived?: boolean;
}

export async function listReceipts(query: ReceiptQuery = {}, signal?: AbortSignal): Promise<ReceiptDto[]> {
  const params = new URLSearchParams();
  if (query.warehouseId) params.set('warehouseId', query.warehouseId);
  if (query.purchaseOrderId) params.set('purchaseOrderId', query.purchaseOrderId);
  if (query.status) params.set('status', query.status);
  if (query.openOnly) params.set('openOnly', 'true');
  if (query.includeArchived) params.set('includeArchived', 'true');

  const qs = params.toString();
  const { data } = await apiRequest<ReceiptDto[]>(`/v1/receipts/${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function getReceipt(id: string, signal?: AbortSignal): Promise<ReceiptDto> {
  const { data } = await apiRequest<ReceiptDto>(`/v1/receipts/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createReceipt(
  body: CreateReceiptRequest,
  signal?: AbortSignal,
): Promise<ReceiptDto> {
  const { data } = await apiRequest<ReceiptDto>('/v1/receipts/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateReceipt(
  id: string,
  body: UpdateReceiptRequest,
  signal?: AbortSignal,
): Promise<ReceiptDto> {
  const { data } = await apiRequest<ReceiptDto>(`/v1/receipts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** Writes the whole delivery to the ledger. All of it or none of it. */
export async function postReceipt(id: string, signal?: AbortSignal): Promise<ReceiptDto> {
  const { data } = await apiRequest<ReceiptDto>(`/v1/receipts/${encodeURIComponent(id)}/post`, {
    method: 'POST',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function cancelReceipt(id: string, signal?: AbortSignal): Promise<ReceiptDto> {
  const { data } = await apiRequest<ReceiptDto>(`/v1/receipts/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listReceiptLines(id: string, signal?: AbortSignal): Promise<ReceiptLineDto[]> {
  const { data } = await apiRequest<ReceiptLineDto[]>(
    `/v1/receipts/${encodeURIComponent(id)}/lines`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Every line write returns the WHOLE set, so the grid is replaced rather than patched. */
export async function saveReceiptLine(
  id: string,
  lineId: string | null,
  body: SaveReceiptLineRequest,
  signal?: AbortSignal,
): Promise<ReceiptLineDto[]> {
  const path = lineId
    ? `/v1/receipts/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`
    : `/v1/receipts/${encodeURIComponent(id)}/lines`;

  const { data } = await apiRequest<ReceiptLineDto[]>(path, {
    method: lineId ? 'PUT' : 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function deleteReceiptLine(
  id: string,
  lineId: string,
  signal?: AbortSignal,
): Promise<ReceiptLineDto[]> {
  const { data } = await apiRequest<ReceiptLineDto[]>(
    `/v1/receipts/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * What is still due on an order.
 *
 * `receiptBatchId` splits the pending total in two — what is waiting in OTHER open batches, and
 * what this batch has already put down — so the screen can show "3 of 10, 2 more at the next door"
 * rather than one number nobody can account for.
 */
export async function listReceivableLines(
  purchaseOrderId: string,
  receiptBatchId?: string,
  signal?: AbortSignal,
): Promise<ReceivableLineDto[]> {
  const qs = receiptBatchId ? `?receiptBatchId=${encodeURIComponent(receiptBatchId)}` : '';
  const { data } = await apiRequest<ReceivableLineDto[]>(
    `/v1/purchase-orders/${encodeURIComponent(purchaseOrderId)}/receivable-lines${qs}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}
